import { Icon } from "@iconify/react"
import { Button, Card, Drawer, Flex, Form, Grid, Input, message, Pagination, Table, Typography } from "antd"
import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import TableAction from "../../components/TableAction";
import { useForm } from "antd/es/form/Form";
import { customerCategoryService } from "../../services/customer-category.service";
import RenderIf from "../../components/RenderIf";
import { fixedExpenseService } from "../../services/fixed-expense.service";


export default function FixedExpenseCost(){

  const breakpoint = Grid.useBreakpoint()

  const [ form ] = useForm()
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();

  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null
  })
  
  const [messageApi, contextHolder] = message.useMessage();
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  const { data: customerCategories } = useQuery({
    queryKey: ['customerCategories'],
    queryFn: () => customerCategoryService.fetchAllWithoutPaginate(),
  })

  const { data: maintenanceCostData , isLoading, isRefetching } = useQuery({
    queryKey: ['meterMaintenanceCost', dataParams],
    queryFn: () => fixedExpenseService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => fixedExpenseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('meterMaintenanceCost');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const storeMutation = useMutation({
    mutationFn: data => fixedExpenseService.storeWithCategory(data),
    onSuccess: () => {
      handleCloseDrawer()
      queryClient.invalidateQueries('meterMaintenanceCost');
      messageApi.success('Data berhasil ditambah');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const updateMutation = useMutation({
    mutationFn: data => fixedExpenseService.updateWithCategory(data),
    onSuccess: () => {
      handleCloseDrawer()
      queryClient.invalidateQueries('meterMaintenanceCost');
      messageApi.success('Data berhasil diubah');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const handleSubmit = val => {
    const formattedBody = {
      size: val.size
    }

    const customerCategories = Object.entries(val)
    .filter(([key]) => key.startsWith("customer_category_"))
    .map(([key, value]) => ({
      id: Number(key.split("_")[2]),
      price: Number(value)
    }));

    formattedBody.customerCategories = customerCategories

    if(drawerOpt.id) updateMutation.mutate({...formattedBody, id: drawerOpt.id})
    else storeMutation.mutate(formattedBody)
  }

  useEffect(() => {
    setTitle("Biaya Beban Tetap");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleUpdate = (record) => {
    form.setFieldsValue(record)

    const catPrice = customerCategories?.data?.map(e => {
      const category = record?.categories?.find(c => c.id == e.id)
      const objname = `customer_category_${e.id}`
      const obj = {}
      obj[objname] = category?.FixedExpenseCostCustomerCategories?.price || null
      return obj
    })

    const catPriceMerged = Object.assign({}, ...catPrice);
    form.setFieldsValue(catPriceMerged)
    setDrawerOpt({ open: true, id: record.id })
    
  }

  const handleCloseDrawer = () => {
    setDrawerOpt({ open: false, id: null })
    form.resetFields()
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      render: (text, record, index) =>  index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Ukuran',
      dataIndex: 'size',
      width: 100,
      fixed: breakpoint.md && 'left',
      align: 'center'
    },
    ...((customerCategories?.data?.length > 0) 
    ? customerCategories.data.map((e) => ({
        title: e.name,
        key: `customer_category_${e.id}`,
        align: 'center',
        width: 100,
        render: record => {
          const category = record?.categories?.find(r => r.id == e.id)
          return category?.FixedExpenseCostCustomerCategories?.price ?? 0
        }
      }))
    : []),
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => handleUpdate(record)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]

  return (
    <>
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        {contextHolder}
        <Flex justify="flex-end" style={{ paddingInline: 24, marginBottom: 24 }} >
          <Button 
            variant="solid" 
            color="primary"  
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => setDrawerOpt({ open: true, id: null })}
          >
            Tambah Ukuran
          </Button>
        </Flex>
        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={maintenanceCostData?.data}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={maintenanceCostData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={maintenanceCostData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <Drawer
        open={drawerOpt.open}
        onClose={handleCloseDrawer}
        title={ (drawerOpt.id ? "Ubah" : "Tambah") + " Data"}
        footer={
          <Flex gap={20} >
            <Button block color="primary" variant="outlined" >
              Batal
            </Button>
            <Button onClick={() => form.submit()} block color="primary" variant="solid" >
              Submit
            </Button>
          </Flex>
        }
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item
            name={'size'}
            label="Ukuran"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Input/>
          </Form.Item>
          <RenderIf when={customerCategories?.data.length != 0} >
            {
              customerCategories?.data?.map((e,i) => (
                <Form.Item label={i == 0 && 'Harga Per Kategori'} name={'customer_category_'+e.id} key={e.id.toString()} >
                  <Input
                    addonBefore={
                      <Typography.Text style={{ display: 'inline-block', width: 20 }} >
                        {e.name}
                      </Typography.Text>
                    }
                  />
                </Form.Item>
              ))
            }
          </RenderIf>
        </Form>
      </Drawer>
    </>
  )
}